import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { TextFromTextValues } from './params';

/**
 * Text built out of text.
 *
 * A cursor walks a path — rows, a spiral, the contour, or a flow field — laying down one
 * character at a time and skipping the parts that fall outside the letterforms. Because
 * the walk is continuous, the filler text stays readable as running text rather than
 * being chopped per glyph, which is what separates this from a masked texture.
 */
export interface PlacedChar {
  char: string;
  x: number;
  y: number;
  rotation: number;
  depth: number;
  index: number;
}

export interface TextFromTextState {
  chars: PlacedChar[];
  palette: ResolvedPalette;
  noise: NoiseFunction2D;
  built: boolean;
}

function values(ctx: TechniqueContext): TextFromTextValues {
  return ctx.values as unknown as TextFromTextValues;
}

export function init(ctx: TechniqueContext): TextFromTextState {
  const v = values(ctx);
  return {
    chars: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    noise: createNoise2D(() => ctx.rng.next()),
    built: false,
  };
}

/**
 * Measures the filler characters once.
 *
 * Canvas text measurement is not cheap, and the walk needs an advance for every step.
 * Measuring the distinct characters up front turns thousands of `measureText` calls
 * into a couple of dozen.
 */
function measureChars(source: string, fontSize: number, tracking: number): Map<string, number> {
  const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(8, 8) : document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  const widths = new Map<string, number>();
  if (!ctx) return widths;

  ctx.font = `${fontSize}px ui-monospace, "SF Mono", Menlo, monospace`;
  for (const ch of new Set(source)) {
    widths.set(ch, ctx.measureText(ch).width + fontSize * tracking);
  }
  return widths;
}

export function step(state: TextFromTextState, _dt: number, ctx: TechniqueContext): TextFromTextState {
  if (state.built) return state;
  const v = values(ctx);

  const source = v.fillText || 'text ';
  const chars = [...source];
  if (chars.length === 0) {
    state.built = true;
    return state;
  }

  const widths = measureChars(source, v.fontSize, v.tracking);
  const lineStep = v.fontSize * v.lineHeight;
  const bounds = ctx.field.bounds;
  if (bounds.width <= 0 || bounds.height <= 0) {
    state.built = true;
    return state;
  }

  let cursor = 0;
  const MAX_CHARS = 220000;

  const emit = (x: number, y: number, rotation: number): boolean => {
    if (state.chars.length >= MAX_CHARS) return false;
    const ch = chars[cursor % chars.length];
    const signed = ctx.field.distance(x, y);
    // Inside test with a half-glyph margin so characters do not hang over the edge.
    const margin = v.edge === 'skip' ? v.fontSize * 0.35 : 0;
    if (signed < -margin) {
      state.chars.push({ char: ch, x, y, rotation, depth: -signed, index: state.chars.length });
      cursor++;
    } else if (v.edge === 'clip') {
      // Advance the cursor anyway, so the filler text keeps flowing across gaps
      // instead of restarting at every counter.
      cursor++;
    }
    return true;
  };

  switch (v.path) {
    case 'vertical': {
      for (let x = bounds.x; x <= bounds.x + bounds.width; x += lineStep) {
        let y = bounds.y;
        while (y <= bounds.y + bounds.height) {
          const w = widths.get(chars[cursor % chars.length]) ?? v.fontSize * 0.6;
          if (!emit(x, y, Math.PI / 2)) break;
          y += w;
        }
      }
      break;
    }

    case 'spiral': {
      const cx = bounds.x + bounds.width / 2;
      const cy = bounds.y + bounds.height / 2;
      const maxR = Math.hypot(bounds.width, bounds.height) / 2 + v.spiralPitch;
      let angle = 0;
      // Archimedean spiral, stepped by arc length so character spacing stays even.
      while (true) {
        const r = (v.spiralPitch * angle) / TAU;
        if (r > maxR) break;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        const w = widths.get(chars[cursor % chars.length]) ?? v.fontSize * 0.6;
        if (!emit(x, y, angle + Math.PI / 2)) break;
        angle += r > 1 ? w / r : 0.4;
      }
      break;
    }

    case 'contour': {
      // Walk inward-offset copies of the outline.
      const rings = Math.max(1, Math.floor(ctx.field.maxInside / v.contourOffset));
      for (let ring = 0; ring < rings; ring++) {
        const inset = (ring + 0.5) * v.contourOffset;
        const perimeter = ctx.field.contours.reduce((sum, c) => sum + c.length, 0);
        const samples = Math.max(16, Math.round(perimeter / Math.max(1, v.fontSize * 0.62)));
        const outline = ctx.field.sampleOutline(samples);
        for (const s of outline) {
          const x = s.x - s.nx * inset;
          const y = s.y - s.ny * inset;
          if (!emit(x, y, Math.atan2(s.ty, s.tx))) break;
        }
      }
      break;
    }

    case 'flow': {
      // Streamlines through a noise field, clipped to the glyphs.
      const starts = ctx.field.samplePoints(700, 'poisson', ctx.rng);
      for (let k = 0; k < starts.length; k += 2) {
        let x = starts[k];
        let y = starts[k + 1];
        for (let stepIndex = 0; stepIndex < 400; stepIndex++) {
          const angle = state.noise(x * v.noiseScale, y * v.noiseScale) * TAU;
          const w = widths.get(chars[cursor % chars.length]) ?? v.fontSize * 0.6;
          if (!emit(x, y, angle)) break;
          x += Math.cos(angle) * w;
          y += Math.sin(angle) * w;
          if (ctx.field.distance(x, y) > v.fontSize) break;
        }
      }
      break;
    }

    default: {
      for (let y = bounds.y; y <= bounds.y + bounds.height; y += lineStep) {
        let x = bounds.x;
        while (x <= bounds.x + bounds.width) {
          const w = widths.get(chars[cursor % chars.length]) ?? v.fontSize * 0.6;
          if (!emit(x, y, 0)) break;
          x += w;
        }
      }
    }
  }

  state.built = true;
  return state;
}

export function isComplete(state: TextFromTextState): boolean {
  return state.built;
}

export function draw(state: TextFromTextState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.textAlign = 'left';
  c.textBaseline = 'middle';
  c.font = `${v.fontSize}px ui-monospace, "SF Mono", Menlo, monospace`;

  const maxDepth = Math.max(1, ctx.field.maxInside);
  const bounds = ctx.field.bounds;

  for (const placed of state.chars) {
    let t: number;
    switch (v.colorMode) {
      case 'position':
        t = bounds.width > 0 ? clamp01((placed.x - bounds.x) / bounds.width) : 0;
        break;
      case 'depth':
        t = clamp01(placed.depth / maxDepth);
        break;
      case 'sequence':
        t = placed.index / Math.max(1, state.chars.length - 1);
        break;
      default:
        t = 0;
    }
    c.fillStyle = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);

    if (placed.rotation !== 0) {
      c.save();
      c.translate(placed.x, placed.y);
      c.rotate(placed.rotation);
      c.fillText(placed.char, 0, 0);
      c.restore();
    } else {
      c.fillText(placed.char, placed.x, placed.y);
    }
  }
}

export function elementCount(state: TextFromTextState): number {
  return state.chars.length;
}
